import { useCallback, useSyncExternalStore } from 'react';
import { useSurface } from '@/contexts/SurfaceContext';

/**
 * "Install app" (spec 2026-10-08-kiosk-web-split §5): the browser's PWA install prompt.
 *
 * Chrome, Edge and Android fire `beforeinstallprompt` once per page load, often before React has
 * mounted and before the surface is known. So the event is captured here at module load (default
 * prevented: the app offers Install app itself instead of the browser's mini-infobar) and kept
 * until it is used. iOS never fires it: there the UI opens the Home Screen guide instead
 * (IosPwaGuideModal). Nothing is offered unless `can('appDownloads')` (never on kiosks or an
 * unknown surface).
 */

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

/** Chromium's BeforeInstallPromptEvent (not in the DOM typings). */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

interface InstallState {
  /** The kept `beforeinstallprompt` event (usable once). */
  deferred: BeforeInstallPromptEvent | null;
  /** `appinstalled` fired in this page. */
  installed: boolean;
}

let state: InstallState = { deferred: null, installed: false };
const listeners = new Set<() => void>();

function setState(next: Partial<InstallState>): void {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
}

function onBeforeInstallPrompt(event: Event): void {
  event.preventDefault();
  setState({ deferred: event as BeforeInstallPromptEvent });
}

function onAppInstalled(): void {
  setState({ deferred: null, installed: true });
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): InstallState {
  return state;
}

/** Test hook: forget a kept event and an install. */
export function __resetInstallPromptForTests(): void {
  setState({ deferred: null, installed: false });
}

/**
 * An iPhone, iPod or iPad (iPadOS reports a desktop Safari user agent, so a "Mac" with touch is
 * an iPad). Any iOS browser adds to the Home Screen through the Share menu, which the guide shows.
 */
export function isIosDevice(): boolean {
  const nav = globalThis.navigator;
  if (!nav) return false;
  const ua = nav.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1;
}

export interface InstallPrompt {
  /** The browser can install the app now (a kept prompt, app downloads allowed, not installed). */
  canInstall: boolean;
  /** iOS, app downloads allowed, not installed: open the Home Screen guide instead of a prompt. */
  isIos: boolean;
  /** Running as the installed app, or installed from this page. */
  isInstalled: boolean;
  /** Shows the browser's install prompt; 'unavailable' when there is none to show. */
  promptInstall: () => Promise<InstallOutcome>;
}

export function useInstallPrompt(): InstallPrompt {
  const { can, isInstalledApp } = useSurface();
  const { deferred, installed } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const allowed = can('appDownloads');
  const isInstalled = isInstalledApp || installed;
  const canInstall = allowed && !isInstalled && deferred !== null;

  const promptInstall = useCallback(async (): Promise<InstallOutcome> => {
    const event = state.deferred;
    if (!canInstall || !event) return 'unavailable';
    // The event can be used once: drop it whatever the answer.
    setState({ deferred: null });
    try {
      await event.prompt();
      const choice = await event.userChoice;
      return choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
    } catch {
      return 'unavailable';
    }
  }, [canInstall]);

  return {
    canInstall,
    isIos: allowed && !isInstalled && isIosDevice(),
    isInstalled,
    promptInstall,
  };
}
